import React from 'react';
import { useFormik } from 'formik';
import { AuthenticationStatus, VERIFICATION_CODE_LENGTH } from '@/contracts';
import { authentication as actions } from '@/actions';
import { Navigate } from 'react-router-dom';
import { RootState, useAppSelector } from '@/state/store';

import { Button, Container } from '@/components';
import { TextField } from '@mui/material';
import { createSelector } from '@reduxjs/toolkit';
import t from '@/utils/translate';

export default ({ loginUrl }: { loginUrl: string }) => {
  const { status, claimId } = useAppSelector(
    createSelector(
      [
        (state: RootState) => state.authentication.status,
        (state: RootState) => state.authentication.claimId,
      ],
      (status, claimId) => ({ status, claimId })
    )
  );

  const formik = useFormik({
    initialValues: { code: '' },
    validate: ({ code }) => {
      const errors: Record<string, string> = {};

      if (!code) {
        errors.code = t('authentication.hint.phoneCode');
      } else if (code.length < VERIFICATION_CODE_LENGTH) {
        errors.code = t('verification.error.codeLength', { length: VERIFICATION_CODE_LENGTH });
      }

      return errors;
    },
    onSubmit: ({ code }) => actions.confirmByPhone(code)
  });

  if (
    !claimId ||
    (status !== AuthenticationStatus.PENDING_CONFIRMATION &&
      status !== AuthenticationStatus.CONFIRMATION)
  ) {
    return <Navigate to={loginUrl} replace/>;
  }

  return (
    <>
      <h3>{t('authentication.hint.smsCode')}</h3>
      <form onSubmit={formik.handleSubmit}>
        <Container margin-v='small'>
          <TextField
            {...formik.getFieldProps('code')}
            disabled={status === AuthenticationStatus.CONFIRMATION}
            required
            label={t('authentication.label.confirmationCode')}
            error={formik.touched.code && !!formik.errors.code}
            helperText={formik.touched.code && formik.errors.code}
            fullWidth
            margin="dense"
            variant="outlined"
            size="small"
            autoFocus
          />
        </Container>
        <Button
          type="submit"
          variant="contained"
          fullWidth
          color="primary"
          size="large"
          disabled={status === AuthenticationStatus.CONFIRMATION}
        >
          {t('action.login')}
        </Button>
      </form>
    </>
  );
};
